import React, { ReactNode } from 'react'
import { Link, LinkProps, useNavigate } from 'react-router-dom'
import { useFeature } from './featureFlagEngine'


// ============================================================================
// FEATURE-AWARE COMPONENTS
// ============================================================================
// Components that automatically hide/disable when features are disabled
// ============================================================================

/**
 * Props for FeatureGuard component
 */
interface FeatureGuardProps {
  featureId?: string
  route?: string
  children: ReactNode
  fallback?: ReactNode
}

/**
 * FeatureGuard - Conditionally renders children based on feature flag
 *
 * @example
 * // Using feature ID
 * <FeatureGuard featureId="attack_surface_domains">
 *   <Button>View Domains</Button>
 * </FeatureGuard>
 *
 * @example
 * // Using route
 * <FeatureGuard route="/attack-surface-explore/Domains">
 *   <Button>View Domains</Button>
 * </FeatureGuard>
 *
 * @example
 * // With fallback
 * <FeatureGuard featureId="chatbot" fallback={<span>Coming soon</span>}>
 *   <Button>Open AI Chat</Button>
 * </FeatureGuard>
 */
export const FeatureGuard: React.FC<FeatureGuardProps> = ({
  featureId,
  route,
  children,
  fallback = null,
}) => {

  const { featureFlagEngine } = useFeature()
  let isEnabled = false
  if (featureId) {

    isEnabled = featureFlagEngine.isFeatureEnabled(featureId)
  } else if (route) {

    isEnabled = featureFlagEngine.isRouteEnabled(route)
  } else {

    return <>{fallback}</>
  }

  return <>{isEnabled ? children : fallback}</>
}

/**
 * Props for FeatureLink component
 */
interface FeatureLinkProps extends Omit<LinkProps, 'to'> {
  to: string
  featureId?: string
  children: ReactNode
  fallback?: ReactNode
  disabled?: boolean
  disabledClassName?: string
}

/**
 * FeatureLink - Link component that only renders if the target route's feature is enabled
 *
 * @example
 * <FeatureLink to="/attack-surface-explore/Domains">
 *   View Domains
 * </FeatureLink>
 *
 * @example
 * // With custom disabled state
 * <FeatureLink
 *   to="/chatbot"
 *   disabledClassName="text-muted cursor-not-allowed"
 *   fallback={<span className="text-muted">AI Chat (Coming Soon)</span>}
 * >
 *   AI Chat
 * </FeatureLink>
 */
export const FeatureLink: React.FC<FeatureLinkProps> = ({
  to,
  featureId,
  children,
  fallback = null,
  disabled = false,
  disabledClassName = 'disabled',
  className = '',
  ...props
}) => {
  const { featureFlagEngine } = useFeature()
  const isEnabled = featureId
    ? featureFlagEngine.isFeatureEnabled(featureId)
    : featureFlagEngine.isRouteEnabled(to)

  if (!isEnabled) {
    return <>{fallback}</>
  }

  if (disabled) {
    return (
      <span className={`${className} ${disabledClassName}`} {...(props as any)}>
        {children}
      </span>
    )
  }

  return (
    <Link to={to} className={className} {...props}>
      {children}
    </Link>
  )
}

/**
 * Props for FeatureButton component
 */
interface FeatureButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  featureId?: string
  route?: string
  navigateTo?: string
  children: ReactNode
  fallback?: ReactNode
}

/**
 * FeatureButton - Button that only renders/works if the feature is enabled
 * Can optionally navigate to a route
 *
 * @example
 * // Simple feature check
 * <FeatureButton featureId="scan_create" onClick={handleCreateScan}>
 *   Create New Scan
 * </FeatureButton>
 *
 * @example
 * // With navigation
 * <FeatureButton navigateTo="/scan-create">
 *   Create New Scan
 * </FeatureButton>
 *
 * @example
 * // With fallback
 * <FeatureButton
 *   featureId="chatbot"
 *   fallback={<button disabled>AI Chat (Coming Soon)</button>}
 * >
 *   Open AI Chat
 * </FeatureButton>
 */
export const FeatureButton: React.FC<FeatureButtonProps> = ({
  featureId,
  route,
  navigateTo,
  children,
  fallback = null,
  onClick,
  ...props
}) => {
  const { featureFlagEngine } = useFeature()
  const navigate = useNavigate()

  let isEnabled = true

  if (featureId) {
    isEnabled = featureFlagEngine.isFeatureEnabled(featureId)
  } else if (route) {
    isEnabled = featureFlagEngine.isRouteEnabled(route)
  } else if (navigateTo) {
    isEnabled = featureFlagEngine.isRouteEnabled(navigateTo)
  }

  if (!isEnabled) {
    return <>{fallback}</>
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (navigateTo) {
      navigate(navigateTo)
    }
    if (onClick) {
      onClick(e)
    }
  }

  return (
    <button onClick={handleClick} {...props}>
      {children}
    </button>
  )
}

/**
 * Hook to check if a feature is enabled
 *
 * @example
 * const isDomainsEnabled = useFeatureEnabled('attack_surface_domains')
 * const isScanEnabled = useFeatureEnabled('/scan-create')
 */
export const useFeatureEnabled = (featureIdOrRoute: string): boolean => {
  const { featureFlagEngine } = useFeature()
  // Check if it's a route (starts with /)
  if (featureIdOrRoute.startsWith('/')) {
    return featureFlagEngine.isRouteEnabled(featureIdOrRoute)
  }
  return featureFlagEngine.isFeatureEnabled(featureIdOrRoute)
}

/**
 * Hook to get multiple feature states at once
 *
 * @example
 * const features = useFeatures(['dashboard', 'scan_create', 'chatbot'])
 * // features = { dashboard: true, scan_create: true, chatbot: false }
 */
export const useFeatures = (featureIds: string[]): Record<string, boolean> => {
  const { featureFlagEngine } = useFeature()
  return featureIds.reduce((acc, id) => {
    acc[id] = featureFlagEngine.isFeatureEnabled(id)
    return acc
  }, {} as Record<string, boolean>)
}

/**
 * Higher-order component to wrap a component with feature flag checking
 *
 * @example
 * const ProtectedComponent = withFeatureFlag('chatbot', () => <div>AI Chat</div>)
 *
 * @example
 * // With fallback
 * const ProtectedComponent = withFeatureFlag(
 *   'chatbot',
 *   () => <div>AI Chat</div>,
 *   () => <div>Coming Soon</div>
 * )
 */
export const withFeatureFlag = <P extends object>(
  featureId: string,
  Component: React.ComponentType<P>,
  Fallback?: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { featureFlagEngine } = useFeature()
    const isEnabled = featureFlagEngine.isFeatureEnabled(featureId)

    if (!isEnabled) {
      return Fallback ? <Fallback {...props} /> : null
    }

    return <Component {...props} />
  }
}

